import * as moment from 'moment'
import { toMoment } from '@tokeet-frontend/tv3-platform'
import { Moment } from 'moment'

export const AIToneOptions = [
  {
    id: 'business' as const,
    label: $localize`Business`,
    example: $localize`E.g. Welcome! Questions about your stay? We're here to help`,
  },
  {
    id: 'casual' as const,
    label: $localize`Casual`,
    example: $localize`E.g. Hey there! Got questions about rooms? Shoot!`,
  },
  {
    id: 'playful' as const,
    label: $localize`Playful`,
    example: $localize`E.g. Hey hey! Curious about our digs? Let's chat!`,
  },
  {
    id: 'thoughtful' as const,
    label: $localize`Thoughtful`,
    example: $localize`E.g. Welcome! Need room info? Just ask, we've got you covered!`,
  },
]

export interface AiResponderDoc {
  id: string
  filename: string
  bytes: number
  preBooking: boolean
  postBooking: boolean
  postCheckin: boolean
  linkedRentals?: string[]
}

export const aiResponderRentalDataSharingItems = [
  { key: 'shareRentalName' as const, label: $localize`Rental Name`, group: 'basic' as const },
  { key: 'shareDisplayName' as const, label: $localize`Display Name`, group: 'basic' as const },
  { key: 'shareCurrency' as const, label: $localize`Currency`, group: 'basic' as const },
  { key: 'shareInquiryCost' as const, label: $localize`Booking Charges`, group: 'basic' as const },
  { key: 'shareEmail' as const, label: $localize`Rental Email Address`, group: 'basic' as const },
  { key: 'shareAddress' as const, label: $localize`Address`, group: 'basic' as const },
  { key: 'sharePostalCode' as const, label: $localize`Postal Code`, group: 'basic' as const },
  { key: 'sharePhone' as const, label: $localize`Phone`, group: 'basic' as const },
  { key: 'shareRegNumber' as const, label: $localize`Registration Number`, group: 'basic' as const },
  { key: 'shareGps' as const, label: $localize`GPS Location`, group: 'basic' as const },

  { key: 'shareType' as const, label: $localize`Rental Type`, group: 'details' as const },
  { key: 'shareCheckInType' as const, label: $localize`Checkin Type`, group: 'details' as const },
  { key: 'shareBedroomCount' as const, label: $localize`Bedroom Count`, group: 'details' as const },
  { key: 'shareBathroomCount' as const, label: $localize`Bathroom Count`, group: 'details' as const },
  { key: 'shareSize' as const, label: $localize`Rental Size`, group: 'details' as const },
  { key: 'shareMinGuestCount' as const, label: $localize`Min Guest Count`, group: 'details' as const },
  { key: 'shareMaxGuestCount' as const, label: $localize`Max Guest Count`, group: 'details' as const },
  { key: 'shareDescription' as const, label: $localize`Description of Property`, group: 'details' as const },
  { key: 'shareAmenities' as const, label: $localize`Amenities Details`, group: 'details' as const },
  { key: 'shareTravellerAge' as const, label: $localize`Traveler Age`, group: 'details' as const },

  { key: 'shareCheckinTime' as const, label: $localize`Checkin time`, group: 'instructions' as const },
  { key: 'shareCheckoutTime' as const, label: $localize`Checkout time`, group: 'instructions' as const },
  { key: 'shareWifi' as const, label: $localize`Wifi Password`, group: 'instructions' as const },
  { key: 'shareSecurityCode' as const, label: $localize`Security Code`, group: 'instructions' as const },
  { key: 'shareKeyPickup' as const, label: $localize`Key Pickup`, group: 'instructions' as const },
  { key: 'shareCheckinInstructions' as const, label: $localize`Checkin Instructions`, group: 'instructions' as const },
  {
    key: 'shareCheckoutInstructions' as const,
    label: $localize`Checkout Instructions`,
    group: 'instructions' as const,
  },
  { key: 'shareDirections' as const, label: $localize`Directions`, group: 'instructions' as const },
  { key: 'shareHouseRules' as const, label: $localize`House Rules`, group: 'instructions' as const },
  { key: 'shareSpecialInstructions' as const, label: $localize`Special Instructions`, group: 'instructions' as const },
  { key: 'sharePaymentTerms' as const, label: $localize`Payment Terms`, group: 'instructions' as const },
  { key: 'sharePaymentInstructions' as const, label: $localize`Payment Instructions`, group: 'instructions' as const },
  { key: 'shareCustomInformations' as const, label: $localize`Custom Informations`, group: 'instructions' as const },
]

type AiResponderRentalDataSharingKey = (typeof aiResponderRentalDataSharingItems)[number]['key']

export interface AIResponderRentalDataSharingItem {
  key: AiResponderRentalDataSharingKey
  label: string
  group: 'basic' | 'details' | 'instructions'
}
export interface AiResponderRentalDataSharingItem {
  isEnabled: boolean
  preBooking: boolean
  postBooking: boolean
  postCheckin: boolean
}

export type AiResponderRentalDataSharing = {
  [key in AiResponderRentalDataSharingKey]: AiResponderRentalDataSharingItem
}

export interface AiResponderContentSharingItem {
  content: string
  preBooking: boolean
  postBooking: boolean
  postCheckin: boolean
}

export enum AiIncidentNotificationType {
  EMAIL = 'email',
  IN_APP = 'in_app',
  SMS = 'sms',
}

export enum AiResponderDetectionSensivity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type AiNotifyMeOption = 'sms' | 'email'

export const AI_OFF_CALENDAR_ITEM_DATE_FORMAT = 'YYYY-MM-DD HH:mm'
export const AI_OFF_CALENDAR_ITEM_TIME_FORMAT = 'HH:mm'

// HH:mm or YYYY-MM-DD:HH:mm
// {from: HH:mm, to: HH:mm} // for all days on the set time range
// {from: YYYY-MM-DD:HH:mm, to: YYYY-MM-DD:HH:mm} // for specific days
export interface AiOffCalendarItem {
  from: string
  to: string
}

export interface AiOffCalendarItemView extends AiOffCalendarItem {
  id: string
  start: moment.Moment
  end: moment.Moment
  all: boolean // if true, the item is for all days on the set time range
}

export function getAiOffCalendarItemDateRange(
  item: AiOffCalendarItem,
  date: number
): { start: Moment; end: Moment; all: boolean } {
  const dateMoment = toMoment(date)
  if (item.from.length === 5) {
    // HH:mm
    const start = moment
      .utc(item.from, AI_OFF_CALENDAR_ITEM_TIME_FORMAT)
      .year(dateMoment.year())
      .month(dateMoment.month())
      .date(dateMoment.date())
    let end = moment
      .utc(item.to, AI_OFF_CALENDAR_ITEM_TIME_FORMAT)
      .year(dateMoment.year())
      .month(dateMoment.month())
      .date(dateMoment.date())

    // If end is 00:00 and start is not 00:00, it means end of day (next day)
    // Or if end is before or equal to start, add a day
    if ((item.to === '00:00' && item.from !== '00:00') || end.isSameOrBefore(start)) {
      end.add(1, 'day')
    }

    return {
      start,
      end,
      all: true,
    }
  } else if (item.from.length >= 10) {
    const start = moment.utc(item.from)
    const end = moment.utc(item.to)
    return {
      start,
      end,
      all: false,
    }
  } else {
    throw new Error('Invalid date format')
  }
}

export function toAiOffCalendarItemView(item: AiOffCalendarItem): AiOffCalendarItemView {
  const { start, end, all } = getAiOffCalendarItemDateRange(item, toMoment().unix())
  return {
    ...item,
    id: item.from + item.to,
    start,
    end,
    all,
  }
}

export enum AiCalendarDateAvailability {
  AVAILABLE = 'available',
  UNAVAILABLE = 'unavailable',
  PARTIALLY_AVAILABLE = 'partially_available',
}

export function isDateAIDisabled(date: number, disabledHours: AiOffCalendarItem[]): AiCalendarDateAvailability {
  const dateStart = toMoment(date).startOf('day')
  const dateEnd = toMoment(date).endOf('day')

  const disabled = disabledHours.some((item) => {
    const { start, end, all } = getAiOffCalendarItemDateRange(item, date)

    // For "all days" items where from is 00:00 and to is 00:00 (full day)
    if (all && item.from === '00:00' && item.to === '00:00') {
      return true // Full day is disabled
    }

    // Check if the entire day is covered
    // Allow for end being at start of next day
    if (start.isSameOrBefore(dateStart, 'minutes') && end.isSameOrAfter(dateEnd, 'minutes')) {
      return true
    }

    return false
  })

  if (disabled) {
    return AiCalendarDateAvailability.UNAVAILABLE
  }

  const isInDisabledRange = disabledHours.some((item) => {
    const { start, end } = getAiOffCalendarItemDateRange(item, date)
    return (
      dateStart.isBetween(start, end, 'minutes', '[)') ||
      dateEnd.isBetween(start, end, 'minutes', '(]') ||
      start.isBetween(dateStart, dateEnd, 'minutes', '[)') ||
      end.isBetween(dateStart, dateEnd, 'minutes', '(]')
    )
  })

  if (isInDisabledRange) {
    return AiCalendarDateAvailability.PARTIALLY_AVAILABLE
  }

  return AiCalendarDateAvailability.AVAILABLE
}

// get the disabled hours for a date
export function getDisabledHoursForDate(date: number, disabledHours: AiOffCalendarItemView[]): AiOffCalendarItemView[] {
  const dateStart = toMoment(date).startOf('day')
  const dateEnd = toMoment(date).endOf('day')

  return disabledHours.filter((item) => {
    const { start, end, all } = getAiOffCalendarItemDateRange(item, date)
    return (
      dateStart.isBetween(start, end, 'minutes', '[)') ||
      dateEnd.isBetween(start, end, 'minutes', '(]') ||
      start.isBetween(dateStart, dateEnd, 'minutes', '[)') ||
      end.isBetween(dateStart, dateEnd, 'minutes', '(]')
    )
  })
}

export type AiResponderSettings = AiResponderRentalDataSharing & { [key: string]: any } & {
  isEnabled: boolean
  isAutomateSendReplies: boolean
  isWebsitesEnabled: boolean
  isDocumentsEnabled: boolean
  isFaqEnabled: boolean

  isContractSigned?: boolean
  contractId?: string

  disabledHours?: AiOffCalendarItem[]
  disabledTimezone?: string

  urls: string[]
  docs?: AiResponderDoc[]
  faqs: AiResponderContentSharingItem[]

  canCreateIncidents?: boolean
  canCreateIncidentsFromReviews?: boolean
  detectionSensivity?: AiResponderDetectionSensivity
  incidentNotifications?: AiIncidentNotificationType[]

  channels: string[]
  delayResponse: number
  isAutoRespondToNewBookings: boolean
  isDelayResponse: boolean
  isRespondEvenIfResponded: boolean
  isCreateDrafts: boolean
  isTranslateEnabled: boolean
  isGenerateGuestSummary: boolean
  tone: string
  name: string
  notifyMe: AiNotifyMeOption[]

  maxReplies: number // Maximum Replies

  ignoreCourtesies: boolean
  // Ignore AI
  ignoreBookings: string[]
  ignoreGuests: string[]
  ignoreGuestsRegexp: string[]
  ignoreRentals: string[]
}

export type UpdateAIResponderSettings = Partial<AiResponderSettings>

export interface AccountTokeetAiSettingsResponse {
  recharge_balance: number
  recharge_threshold: number
}
